import { useState, useEffect, useContext, useMemo } from "react";
import { GlobalContext } from "./GlobalContext";
import fields from "./fields";
import { isDealSheet, isHiddenByDefault, buildDefaultOrder } from "./defaultSheetLayout";

// Pairwise comparison: same length and each {name,hidden} entry matches.
function sameOrder(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].name !== b[i].name || a[i].hidden !== b[i].hidden) return false;
    }
    return true;
}

/**
 * `defaultKeys` is only a placeholder for the header of a sheet whose data has
 * not arrived yet. It must not survive into the final order, or the layout ends
 * up depending on whether the fetch happened to land before the component
 * mounted: mounting with data derives the order from the row, mounting empty
 * would keep defaultKeys and append everything else behind them.
 *
 * That was unreachable while SubNav fetched all nine sheets at login, because a
 * sheet component always mounted with data already in the cache. Sheets now
 * load per tab, so without the wholesale rebuild below two users with identical
 * preferences would see different column orders depending on network timing.
 */
export default function useFieldOrder(sheetName, firstRow, defaultKeys = []) {
    const { fieldOrder: globalFieldOrder, hiddenCostFields } = useContext(GlobalContext);

    const saved = globalFieldOrder?.[sheetName];
    const hasSavedPref = !!saved?.length;

    const [fieldOrder, setFieldOrder] = useState(() => {
        if (hasSavedPref) return saved;
        //Pref-less deal sheet with data already present → curated default.
        if (firstRow && isDealSheet(sheetName)) return buildDefaultOrder(sheetName, firstRow);
        const keys = firstRow ? Object.getOwnPropertyNames(firstRow) : defaultKeys;
        return keys.map(name => ({ name, hidden: false }));
    });

    useEffect(() => {
        if (!firstRow) return;
        //A pref-less deal-sheet user's real default only forms once data arrives
        //(initial state was defaultKeys at mount) — rebuild wholesale, don't append.
        //Use functional update + sameOrder guard so refetches that return the same
        //schema don't produce a new array reference and re-render all rows.
        if (!hasSavedPref && isDealSheet(sheetName)) {
            setFieldOrder(prev => {
                const built = buildDefaultOrder(sheetName, firstRow);
                return sameOrder(prev, built) ? prev : built;
            });
            return;
        }
        setFieldOrder(prev => {
            //No stored preference: the row is the whole truth, so rebuild rather
            //than appending to the placeholder. Guarded by sameOrder so a refetch
            //returning the same schema keeps the array identity and does not
            //re-render every row.
            if (!hasSavedPref) {
                const built = Object.getOwnPropertyNames(firstRow).map(name => ({ name, hidden: false }));
                return sameOrder(prev, built) ? prev : built;
            }
            if (!prev.length) {
                return Object.getOwnPropertyNames(firstRow).map(name => ({ name, hidden: false }));
            }
            const existingNames = new Set(prev.map(f => f.name));
            const newFields = Object.getOwnPropertyNames(firstRow)
                .filter(name => !existingNames.has(name) && fields[name])
                .map(name => ({ name, hidden: isDealSheet(sheetName) ? isHiddenByDefault(name, sheetName) : false }));
            return newFields.length ? [...prev, ...newFields] : prev;
        });
    }, [firstRow, hasSavedPref, sheetName]);

    //Phase 8: drop cost columns the user isn't allowed to see (server already
    //nulls them). Filtered on the way out so the stored order/prefs are intact.
    //Memoized so visibleOrder keeps its identity when fieldOrder and hiddenCostFields haven't changed.
    const visibleOrder = useMemo(
        () => (hiddenCostFields && hiddenCostFields.size)
            ? fieldOrder.filter(f => !hiddenCostFields.has(f.name))
            : fieldOrder,
        [fieldOrder, hiddenCostFields]
    );

    return [visibleOrder, setFieldOrder];
}
